import React, { useState, useEffect, useCallback, useId } from 'react';
import axios from 'axios';
import { refusalMessage, currentLang } from './docText';
// ONEFORM: every visible string in this form goes through i18n, in English and French. The
// form had NO i18n at all before: its copy was hardcoded English, which is why the texts
// points 5 and 6 ask for had nowhere to live.
import { useTranslation } from 'react-i18next';
import { useDarkMode } from './DarkModeContext';
import CertificateUpload from './CertificateUpload';
// WFHLIST point 2: the searchable country field, shared with the absence register's filter.
import CountryTypeahead from './CountryTypeahead';
import { GOLD, NAVY_FORM, NAVY_SHELL, TEXT_DARK_MODE, TEXT_MUTED } from './colors';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';
// NAVY kept as a local alias (referenced below); GOLD now comes straight from the shared module.
const NAVY = NAVY_SHELL;

// ONEFORM: the label is an i18n KEY now, resolved at render. The English wording is
// unchanged, so nothing a reader sees moves in English while French becomes possible.
const LEAVE_TYPE_OPTIONS = [
  { value: 'holiday', key: 'leaveForm.typeHoliday' },
  { value: 'sick',    key: 'leaveForm.typeSick' },
  { value: 'wfh',     key: 'leaveForm.typeWfh' },
];

// ONEFORM point 3: the threshold the form falls back to only when the server has not
// answered yet. It is NOT a second source of truth - the real value is the entity's, read
// from /leave/mine's rows or /leave/certificate-threshold, and this constant exists so the
// field has a defined state during the first render rather than flashing the wrong rule.
const THRESHOLD_FALLBACK = 7;

// ONEFORM point 3: inclusive day count, the SAME arithmetic the server's absence_days uses -
// a one-day absence is 1 day, not 0. Getting this wrong by one would move the whole
// threshold boundary, which is why it is a named function and not an inline subtraction.
function absenceDays(start, end) {
  if (!start || !end) return 0;
  const a = new Date(start);
  const b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

const SYNC_STATUS_META = {
  synced:  { bg: '#d1fae5', color: '#065f46', label: 'Synced to Outlook' },
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Syncing…' },
  failed:  { bg: '#fee2e2', color: '#991b1b', label: 'Not synced to Outlook' },
  // CALSAFE: the status a declaration gets outside production, where the calendar is never
  // written. Needed as its own entry because the lookup below FALLS BACK TO `pending` for an
  // unknown status, so without this a dev row would read "Syncing..." forever and look like a
  // stuck job rather than a deliberate skip. Neutral grey, because nothing is wrong.
  not_synced_dev: { bg: '#e5e7eb', color: '#4b5563', label: 'Not added to Outlook (development)' },
};

const APPROVAL_STATUS_META = {
  pending:  { bg: '#fef3c7', color: '#92400e', label: 'Pending approval' },
  approved: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
  // LEAVECANCEL: set when a still-pending holiday is withdrawn by the employee. Without this key
  // the badge fell through to the `pending` default and a withdrawn holiday kept reading "Pending
  // approval" forever, describing a decision nobody would ever make.
  withdrawn: { bg: '#e5e7eb', color: '#4b5563', label: 'Withdrawn' },
};

// LEAVECANCEL: the declaration's own lifecycle, shown ALONGSIDE the approval badge rather than
// instead of it - the two answer different questions and a cancelled holiday that had been
// approved should still show that it was once approved. `active` renders nothing: it is the
// ordinary case and a badge saying "Active" on every row would be noise.
const CANCEL_STATUS_META = {
  cancel_pending: { bg: '#fef3c7', color: '#92400e', label: 'Cancellation requested' },
  cancelled:      { bg: '#e5e7eb', color: '#4b5563', label: 'Cancelled' },
};

// ONEFORM point 4: wfh_country is sent only when the box is ticked, so an untouched form
// posts exactly what it posted before.
const EMPTY = { leave_type: 'holiday', start_date: '', end_date: '', notes: '', wfh_country: '' };

function fmt(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

function SyncBadge({ status, error }) {
  const m = SYNC_STATUS_META[status] || SYNC_STATUS_META.pending;
  return (
    <span
      title={status === 'failed' && error ? error : undefined}
      style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}
    >
      {m.label}
    </span>
  );
}

function ApprovalBadge({ status }) {
  const m = APPROVAL_STATUS_META[status] || APPROVAL_STATUS_META.pending;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

function CancelBadge({ status }) {
  const m = CANCEL_STATUS_META[status];
  if (!m) return null;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

export default function LeaveDeclarationForm() {
  const { isDarkMode } = useDarkMode();
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  // ONEFORM point 3: the file chosen IN THE FORM, before any declaration exists. Held in
  // state rather than read off the input at submit time, so the required-field check can see
  // whether one has been chosen.
  const [certFile, setCertFile] = useState(null);
  const [threshold, setThreshold] = useState(THRESHOLD_FALLBACK);
  // ONEFORM point 4: the counter, and the country picker's own state.
  const [wfhUsed, setWfhUsed] = useState(null);
  const [wfhLimit, setWfhLimit] = useState(2);
  const [fromAnotherCountry, setFromAnotherCountry] = useState(false);
  const [countries, setCountries] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  // LEAVECANCEL: a SUCCESS message, separate from `error`. Cancelling now has two different
  // successful outcomes (cancelled outright, or a request now awaiting the manager) and the person
  // has to be told which one happened - the row's badge alone does not explain that a manager is
  // now involved and that the leave still stands meanwhile.
  const [notice, setNotice] = useState('');

  const cardBg = isDarkMode ? NAVY_SHELL : '#ffffff';
  const textCol = isDarkMode ? TEXT_DARK_MODE : NAVY_FORM;
  const mutedCol = isDarkMode ? TEXT_MUTED : '#666';
  const border = isDarkMode ? '1px solid rgba(201,168,76,0.25)' : '1px solid #e2e6ec';
  const rowBorder = isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f0f0f0';
  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: '6px',
    border: isDarkMode ? '1px solid rgba(201,168,76,0.3)' : '1px solid #cdd4de',
    background: isDarkMode ? '#243058' : '#fff', color: textCol,
    fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: textCol, margin: '0 0 6px' };
  const groupStyle = { marginBottom: '18px' };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // MEDLINK point 3: the missing-certificate email links to /leave?declaration=<id>. Reading it
  // here selects that row and scrolls to its upload action, so the person lands on the thing they
  // were asked to do rather than on a list they have to search.
  const deepLinkId = React.useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('declaration') || '';
    } catch (e) {
      return '';
    }
  }, []);
  const deepLinkRef = React.useRef(null);

  useEffect(() => {
    if (!deepLinkId || !deepLinkRef.current) return;
    // Only scroll once the row actually exists, which is after the declarations have loaded.
    deepLinkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [deepLinkId, declarations]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/leave/mine`)
      .then(res => {
        setDeclarations(res.data);
        setLoadError('');
        // ONEFORM point 3: the entity's threshold, carried on every row. Identical on all of
        // them because it is a property of the caller's own entity, so the first row answers
        // it. An EMPTY list cannot, which is what the dedicated endpoint below is for.
        const rows = Array.isArray(res && res.data) ? res.data : [];
        const fromRow = rows.find(r => Number.isFinite(r && r.certificate_threshold_days));
        if (fromRow) setThreshold(fromRow.certificate_threshold_days);
      })
      .catch(() => { setDeclarations([]); setLoadError('Could not load your declarations - please refresh.'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // ONEFORM: a GET that cannot throw and cannot return undefined.
  //
  // WHY THIS EXISTS, found by running the existing suites. The two reads this build adds are
  // SUPPLEMENTARY: the form must work without them (it has a threshold fallback and simply hides
  // the counter). But LeaveDeclarationForm.cancel.test.js queues its axios responses with
  // mockResolvedValueOnce, so an extra call returns undefined and `.then` on undefined threw,
  // breaking seven tests about cancellation that have nothing to do with this feature.
  //
  // THAT WAS A REAL FRAGILITY IN MY CODE, not merely a test artefact: any caller, stub or
  // interceptor that returns a non-promise would have taken the whole form down with it. A
  // supplementary read must never be able to break the screen it decorates.
  const safeGet = useCallback((url, config) => {
    try {
      const p = axios.get(url, config);
      return p && typeof p.then === 'function' ? p : Promise.resolve(null);
    } catch (e) {
      return Promise.resolve(null);
    }
  }, []);

  // ONEFORM point 3: the threshold for somebody with NO declarations yet - who is exactly the
  // person most likely to be declaring their first sick leave. Without this the form would
  // fall back to the default and could show a South Africa employee (threshold 2) the UK rule.
  // Failure is silent and leaves the fallback in place: a form that will not open because a
  // threshold lookup failed is worse than one showing the commonest value.
  useEffect(() => {
    safeGet(`${API}/leave/certificate-threshold`)
      .then(res => {
        const d = res && res.data && res.data.days;
        if (Number.isFinite(d)) setThreshold(d);
      })
      .catch(() => {});
  }, []);

  // ONEFORM point 4: the counter and the country list, fetched ONLY when WFH is selected, so
  // somebody declaring a holiday makes no extra request. The counter reads the CURRENT
  // calendar month, which is what "this month" means on the label.
  useEffect(() => {
    if (form.leave_type !== 'wfh') return;
    const now = new Date();
    safeGet(`${API}/leave/wfh-count`,
            { params: { year: now.getFullYear(), month: now.getMonth() + 1 } })
      .then(res => {
        if (res && res.data && Number.isFinite(res.data.used)) setWfhUsed(res.data.used);
        if (res && res.data && Number.isFinite(res.data.limit)) setWfhLimit(res.data.limit);
      })
      .catch(() => setWfhUsed(null));
    safeGet(`${API}/leave/wfh-countries`)
      .then(res => setCountries((res && res.data && res.data.countries) || []))
      .catch(() => setCountries([]));
  }, [form.leave_type]);

  // ONEFORM point 3: does the absence being typed REQUIRE a certificate? Derived on every
  // render from the dates and the entity threshold, so the field appears the moment the dates
  // pass it rather than after a save or a reload.
  const days = absenceDays(form.start_date, form.end_date);
  // CERTGATE: the threshold is COERCED TO A NUMBER before it is compared or displayed.
  //
  // WHY, found while reproducing the reported bug. `days > threshold` happens to survive a string
  // ("8" > "7" is true by coercion), but `threshold + 1` in the sick-leave explanation does NOT:
  // with a string it reads "A medical certificate is required from day 71". Both readers guard
  // with Number.isFinite today, so a string cannot get in through them - but the value crosses a
  // JSON boundary from a column whose type a later migration could change, and one coercion here
  // is cheaper than trusting every future producer of it.
  const thresholdDays = Number(threshold);
  const effectiveThreshold = Number.isFinite(thresholdDays) && thresholdDays > 0
    ? thresholdDays
    : THRESHOLD_FALLBACK;
  // STRICTLY GREATER: 7 calendar days inclusive is OPTIONAL, the 8th day makes it REQUIRED.
  const certRequired = form.leave_type === 'sick' && days > effectiveThreshold;
  const certOptional = form.leave_type === 'sick' && !certRequired;

  const submit = async () => {
    setError('');
    setNotice('');
    if (!form.start_date || !form.end_date) { setError(t('leaveForm.errorDates')); return; }
    if (form.end_date < form.start_date) { setError(t('leaveForm.errorOrder')); return; }
    // ONEFORM point 3: a REQUIRED certificate is required BEFORE the declaration is created, so
    // the person is not left with a saved sick leave and an immediate obligation they did not see
    // coming. Below the threshold the field is optional and this check does not apply.
    if (certRequired && !certFile) {
      setError(t('leaveForm.certificateRequiredHint', { days: effectiveThreshold }));
      return;
    }
    // ONEFORM point 4: the country is only meaningful when the box is ticked, and when it IS
    // ticked a country has to be chosen - the server refuses an unlisted value, so catching it
    // here turns a 400 into a sentence next to the field.
    if (form.leave_type === 'wfh' && fromAnotherCountry && !form.wfh_country) {
      setError(t('leaveForm.errorCountry'));
      return;
    }
    setSubmitting(true);
    try {
      // The payload carries wfh_country ONLY for a WFH declaration with the box ticked. Every
      // other combination posts exactly what this form posted before this build.
      const payload = { ...form };
      if (!(payload.leave_type === 'wfh' && fromAnotherCountry)) payload.wfh_country = '';
      // CERTGATE: tell the server a certificate is coming for this declaration. The server refuses
      // a sick leave longer than the entity threshold without this, which is what closes the
      // bypass: the flag is only ever set here, AFTER the required-certificate check above has
      // passed, and that check only passes with a file in hand. It is sent for the optional case
      // too when a file was attached, because the file genuinely is coming.
      payload.certificate_to_follow = !!certFile;
      const res = await axios.post(`${API}/leave/`, payload);

      // ONEFORM point 3: THE FILE IS ATTACHED AFTER THE DECLARATION EXISTS, through the EXISTING
      // MEDLINK route, with leave_declaration_id set - the same route, authorisation, storage and
      // audit CertificateUpload already uses on a saved row. No new upload path is introduced.
      //
      // THE DECLARATION SURVIVES A FAILED UPLOAD, which is the whole point of doing it in this
      // order. The absence is the record that matters and it is already saved; the person is told
      // to attach the file from the declaration line, where the existing action stays. Rolling the
      // declaration back instead would lose a sick leave because a file transfer failed.
      let uploadFailed = false;
      let uploadReason = '';
      const newId = res && res.data && (res.data.id || res.data.declaration_id);
      if (certFile && newId) {
        try {
          const fd = new FormData();
          fd.append('employee_id', (res.data && res.data.employee_id) || '');
          fd.append('document_type', 'medical_certificate');
          fd.append('leave_declaration_id', newId);
          fd.append('file', certFile);
          await axios.post(`${API}/documents/upload`, fd);
        } catch (uploadErr) {
          uploadFailed = true;
          // UPLOADREFUSE: keep the server's real reason to show with the message.
          uploadReason = refusalMessage(uploadErr, currentLang(), 'uploadChecks.uploadFailed');
        }
      }

      setForm(EMPTY);
      setCertFile(null);
      setFromAnotherCountry(false);
      if (uploadFailed) {
        setError(`${t('leaveForm.certificateUploadFailed')} ${uploadReason}`.trim());
      }
      load();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || t('leaveForm.errorSave'));
    } finally {
      setSubmitting(false);
    }
  };

  // LEAVECANCEL: cancelling no longer removes the row, so the list is RELOADED rather than having
  // the row filtered out of it. The person must be able to see what happened to the declaration -
  // either it now reads "Cancelled", or it reads "Cancellation requested" because their manager
  // has to agree first. Filtering it away, as the old delete did, is exactly the disappearance
  // this work exists to stop.
  const cancel = async (id) => {
    setError('');
    setDeletingId(id);
    try {
      const res = await axios.delete(`${API}/leave/${id}`);
      if (res.data && res.data.status === 'cancel_pending') {
        setNotice('Your manager has been asked to approve this cancellation. Until they do, the leave still stands and stays on the shared calendar.');
      } else {
        setNotice('This leave has been cancelled. It no longer counts against you.');
      }
      load();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || 'Could not cancel this declaration. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
      {/* ── Declare form ── */}
      <div style={{ background: cardBg, border, borderRadius: '10px', padding: '28px', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: textCol, margin: '0 0 6px' }}>{t('leaveForm.title')}</h2>
        <div style={{ height: '3px', width: '48px', background: GOLD, borderRadius: '2px', margin: '0 0 16px' }} />
        <p style={{ fontSize: '13.5px', lineHeight: 1.6, color: mutedCol, margin: '0 0 20px' }}
           data-testid="leave-explain">
          {/* ONEFORM point 5: ONE SENTENCE PER TYPE, AND NO MENTION OF ANY CALENDAR. The copy this
              replaces promised the absence "will also be added to the shared <type> team
              calendar". LEAVE_CALENDAR_ENABLED is false, so nobody can open that calendar: telling
              somebody their sick leave appears on one that does not exist for them is worse than
              saying nothing, and it was the most misleading sentence on this screen. The sick text
              states the day a certificate becomes required, which is threshold + 1. */}
          {form.leave_type === 'holiday' && t('leaveForm.explainHoliday')}
          {form.leave_type === 'sick' && t('leaveForm.explainSick', { day: effectiveThreshold + 1 })}
          {form.leave_type === 'wfh' && t('leaveForm.explainWfh', { limit: wfhLimit })}
        </p>

        <div style={groupStyle}>
          <label style={labelStyle} htmlFor={`${a11yId}-type`}>{t('leaveForm.typeLabel')}</label>
          
          <select id={`${a11yId}-type`} style={inputStyle} value={form.leave_type} onChange={e => set('leave_type', e.target.value)}>
            {LEAVE_TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{t(opt.key)}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ ...groupStyle, flex: 1 }}>
            <label style={labelStyle} htmlFor={`${a11yId}-start-date`}>{t('leaveForm.startDate')}</label>
            
            <input id={`${a11yId}-start-date`} style={inputStyle} type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div style={{ ...groupStyle, flex: 1 }}>
            <label style={labelStyle} htmlFor={`${a11yId}-end-date`}>{t('leaveForm.endDate')}</label>
            
            <input id={`${a11yId}-end-date`} style={inputStyle} type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
          </div>
        </div>

        {/* ── ONEFORM point 4: WFH only ── */}
        {form.leave_type === 'wfh' && (
          <div style={groupStyle} data-testid="wfh-block">
            {/* The counter for the CURRENT calendar month, which is what "this month" means. It is
                not shown until the server has answered: a counter reading "0 of 2" while the real
                figure is still loading would be a wrong number, not a blank one. */}
            {wfhUsed !== null && (
              <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: textCol }}
                 data-testid="wfh-counter">
                {t('leaveForm.wfhCounter', { used: wfhUsed, limit: wfhLimit })}
              </p>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: textCol, cursor: 'pointer' }}>
              <input
                type="checkbox"
                data-testid="wfh-from-another-country"
                checked={fromAnotherCountry}
                onChange={e => {
                  setFromAnotherCountry(e.target.checked);
                  if (!e.target.checked) set('wfh_country', '');
                }}
              />
              {t('leaveForm.wfhFromAnotherCountry')}
            </label>
            {/* WFHLIST point 2: a SEARCHABLE field, not a dropdown. The component owns the
                filtering, the keyboard behaviour and the ARIA wiring; what comes back through
                onChange is always a stored country or '', never typed text, so a free value can
                never reach the payload. */}
            {fromAnotherCountry && (
              <div style={{ marginTop: '10px' }}>
                <CountryTypeahead
                  id={`${a11yId}-wfh-country`}
                  countries={countries}
                  value={form.wfh_country}
                  onChange={(v) => set('wfh_country', v)}
                  inputStyle={inputStyle}
                  labelStyle={labelStyle}
                  dark={isDarkMode}
                />
              </div>
            )}
          </div>
        )}

        {/* ── ONEFORM point 3: the certificate field, IN THE FORM ── */}
        {(certRequired || certOptional) && (
          <div style={groupStyle} data-testid="cert-field">
            <label style={labelStyle} htmlFor={`${a11yId}-certificate`}>
              {certRequired ? t('leaveForm.certificateLabel') : t('leaveForm.certificateOptionalLabel')}
              {' '}
              <span style={{ fontWeight: 400, color: certRequired ? '#A63A3A' : mutedCol }}
                    data-testid="cert-badge">
                ({certRequired ? t('leaveForm.certificateRequiredBadge') : t('leaveForm.certificateOptionalBadge')})
              </span>
            </label>
            {certRequired && (
              <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: '#A63A3A', lineHeight: 1.5 }}
                 data-testid="cert-required-hint">
                {t('leaveForm.certificateRequiredHint', { days: effectiveThreshold })}
              </p>
            )}
            <input
              id={`${a11yId}-certificate`}
              data-testid="cert-input"
              type="file"
              required={certRequired}
              aria-required={certRequired ? 'true' : 'false'}
              style={{ ...inputStyle, padding: '8px' }}
              onChange={e => setCertFile((e.target.files && e.target.files[0]) || null)}
            />
          </div>
        )}
        <div style={groupStyle}>
          <label style={labelStyle} htmlFor={`${a11yId}-notes-optional`}>{t('leaveForm.notes')} <span style={{ color: mutedCol, fontWeight: 400 }}>{t('leaveForm.notesOptional')}</span></label>
          <textarea id={`${a11yId}-notes-optional`} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
          {/* ONEFORM point 6: under the notes field, for EVERY type. An employee typing a
              diagnosis into a free-text box is how medical detail reaches a record that HR, a
              manager and the COO can all read, and the field cannot be removed because it is
              legitimately used for everything else. */}
          <p style={{ margin: '6px 0 0', fontSize: '12px', color: mutedCol }} data-testid="notes-warning">
            {t('leaveForm.notesWarning')}
          </p>
        </div>

        {error && <div style={{ fontSize: '13px', color: '#A63A3A', marginBottom: '14px' }} role="alert" aria-live="polite">{error}</div>}

        <button
          onClick={submit}
          disabled={submitting}
          style={{
            background: submitting ? '#8a94a8' : NAVY, color: '#fff', border: `1px solid ${GOLD}`,
            borderRadius: '6px', padding: '12px 24px', fontSize: '14px', fontWeight: 700,
            cursor: submitting ? 'default' : 'pointer',
          }}
        >
          {submitting ? t('leaveForm.submitting') : (form.leave_type === 'holiday' ? t('leaveForm.submitPropose') : t('leaveForm.submitDeclare'))}
        </button>
      </div>

      {/* ── My declarations ── */}
      <div style={{ background: cardBg, border, borderRadius: '10px', padding: '28px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 700, color: textCol, margin: '0 0 16px' }}>My declarations</h3>
        {loadError && <div style={{ fontSize: '13px', color: '#A63A3A', marginBottom: '14px' }} role="alert" aria-live="polite">{loadError}</div>}
        {notice && <div style={{ fontSize: '13px', color: '#065f46', background: '#d1fae5', border: '1px solid #a7f3d0', borderRadius: '6px', padding: '10px 12px', marginBottom: '14px' }} role="status" aria-live="polite">{notice}</div>}
        {loading ? (
          <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px' }}>Loading…</p>
        ) : declarations.length === 0 ? (
          <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px' }}>No declarations yet.</p>
        ) : (
          declarations.map(d => {
            const typeLabel = LEAVE_TYPE_OPTIONS.find(o => o.value === d.leave_type)?.label || d.leave_type;
            const status = d.status || 'active';
            // LEAVECANCEL: `cancel_regime` is computed SERVER side (see leave.py's _cancel_regime,
            // the same function the endpoint enforces with), so this never restates the rule and
            // the two cannot drift. 'refused' means sick leave whose start date has passed.
            const locked = d.cancel_regime === 'refused';
            const canAct = status === 'active' && !locked;
            return (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: rowBorder, gap: '12px' }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: textCol, textDecoration: status === 'cancelled' ? 'line-through' : 'none' }}>{typeLabel}</p>
                  <p style={{ margin: 0, fontSize: '12px', color: mutedCol }}>
                    {fmt(d.start_date)} → {fmt(d.end_date)}
                    {d.notes ? ` · ${d.notes}` : ''}
                  </p>
                  {/* WHAT THE PERSON SEES WHILE A REQUEST IS PENDING. The badge alone says a
                      cancellation was requested; this line says what that MEANS for them right
                      now, which is the part they would otherwise have to guess: the leave has not
                      gone away, and they are still booked off until the manager decides. */}
                  {status === 'cancel_pending' && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: mutedCol, lineHeight: 1.5 }}>
                      Waiting for your manager to approve this cancellation. Until they do, this
                      leave still stands and stays on the shared calendar.
                    </p>
                  )}
                  {locked && status === 'active' && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: mutedCol, lineHeight: 1.5 }}>
                      Sick leave cannot be cancelled once it has started. Speak to HR if it was
                      entered in error.
                    </p>
                  )}
                  {/* MEDLINK point 2: the employee's own certificate action, on their own sick
                      leave. Always offered for a sick row, highlighted when one is required or
                      missing. The component returns null for every other leave type. */}
                  <CertificateUpload
                    declaration={d}
                    employeeId={d.employee_id}
                    dark={isDarkMode}
                    onUploaded={load}
                    highlightRef={d.id === deepLinkId ? deepLinkRef : undefined}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                  {d.leave_type === 'holiday' && <ApprovalBadge status={d.approval_status} />}
                  <CancelBadge status={status} />
                  <SyncBadge status={d.outlook_sync_status} error={d.outlook_sync_error} />
                  {canAct && (
                    <button
                      onClick={() => cancel(d.id)}
                      disabled={deletingId === d.id}
                      // The label states which of the two outcomes this click produces, so nobody
                      // presses "Cancel" expecting it to be immediate and finds a manager now has
                      // to agree. Driven by the same server-computed regime as the guard above.
                      title={d.cancel_regime === 'request'
                        ? 'This leave is already agreed, so your manager has to approve the cancellation'
                        : 'This will cancel the leave straight away'}
                      style={{
                        background: 'none', border: `1px solid ${isDarkMode ? 'rgba(166,58,58,0.5)' : '#e0b4b4'}`,
                        color: '#A63A3A', borderRadius: '6px', padding: '5px 10px', fontSize: '12px',
                        cursor: deletingId === d.id ? 'default' : 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      {deletingId === d.id ? '…' : (d.cancel_regime === 'request' ? 'Request cancellation' : 'Cancel')}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
